import { createReducer, on } from '@ngrx/store';
import { Company } from '@src/app/core/types/company.type';
import { Contract } from '@src/app/core/types/contract.type';
import { User } from '@src/app/core/types/user.type';
import { AuthActions } from './auth.actions';

export interface AuthState {
  kam?: any;
  loadingKam: boolean;
  user?: User;
  loadingUser: boolean;
  activeContracts: Contract[];
  loadingContracts: boolean;
  companiesParams: {
    page?: number;
    limit?: number;
    name?: string;
  };
  loadingCompanies?: boolean;
  selectedCompany: Company | null;
  changingCompany: boolean;
  showErrorLogin: boolean;
  declaracionApiToken: string;
  loadingDeclaracionApiToken: boolean;
  loadingCIF: boolean;
  loadingCIFError?: boolean;
}

const companiesParamsDefault = {
  page: 1,
  limit: 10,
  name: '',
};

let initialState: AuthState = {
  kam: undefined,
  loadingKam: false,
  loadingUser: false,
  activeContracts: [],
  loadingContracts: false,
  companiesParams: companiesParamsDefault,
  loadingCompanies: false,
  selectedCompany: null,
  changingCompany: false,
  showErrorLogin: false,
  declaracionApiToken: '',
  loadingDeclaracionApiToken: false,
  loadingCIF: false,
  loadingCIFError: false,
};

export const authReducer = createReducer(
  initialState,
  on(AuthActions.loadUserInfo, (state) => {
    return {
      ...state,
      loadingUser: true,
    };
  }),
  on(AuthActions.loadUserInfoSuccess, (state, { user }) => {
    return {
      ...state,
      user: user,
      loadingUser: false,
    };
  }),
  on(AuthActions.loadUserInfoFailure, (state) => {
    return {
      ...state,
      loadingUser: false,
    };
  }),
  on(AuthActions.loadCompanyCIF, (state) => {
    if (state.selectedCompany?.CIF_NIF) return state;
    return { ...state, loadingCIF: true };
  }),
  on(AuthActions.loadCompanyCIFSuccess, (state, { cif }) => {
    return {
      ...state,
      loadingCIF: false,
      loadingCIFError: false,
      selectedCompany: state.selectedCompany
        ? { ...state.selectedCompany, CIF_NIF: cif }
        : null,
    };
  }),
  on(AuthActions.loadCompanyCIFFailure, (state) => {
    return { ...state, loadingCIF: false, loadingCIFError: true };
  }),
  on(AuthActions.loadCompanies, (state) => {
    return {
      ...state,
      loadingCompanies: true,
    };
  }),
  on(AuthActions.loadCompaniesSuccess, (state, { companies, pagination }) => ({
    ...state,
    user: state.user
      ? {
          ...state.user,
          EMPRESAS: [...(state.user.EMPRESAS || []), ...companies],
          PAGINATION: pagination ? pagination : state.user.PAGINATION,
        }
      : state.user,
    loadingCompanies: false,
  })),
  on(AuthActions.loadCompaniesFailure, (state) => {
    return {
      ...state,
      loadingCompanies: false,
    };
  }),
  on(AuthActions.setSelectedCompany, (state, { company }) => {
    return {
      ...state,
      changingCompany: true,
    };
  }),
  on(AuthActions.changeCompanySuccess, (state, { company }) => {
    return {
      ...state,
      selectedCompany: company,
      changingCompany: false,
      loadingCIFError: false,
    };
  }),
  on(AuthActions.changeCompanyFailure, (state) => {
    return {
      ...state,
      changingCompany: false,
    };
  }),
  on(AuthActions.loadKam, (state) => ({
    ...state,
    loadingKam: true,
  })),
  on(AuthActions.loadKamSuccess, (state, { kam }) => ({
    ...state,
    kam,
    loadingKam: false,
  })),
  on(AuthActions.loadKamFailure, (state) => ({
    ...state,
    loadingKam: false,
  })),
  on(AuthActions.loadActiveContracts, (state) => ({
    ...state,
    loadingContracts: true,
  })),
  on(AuthActions.loadActiveContractsSuccess, (state, { contracts }) => ({
    ...state,
    activeContracts: contracts,
    loadingContracts: false,
  })),
  on(AuthActions.loadActiveContractsFailure, (state) => ({
    ...state,
    loadingContracts: false,
  })),
  on(AuthActions.loadCompanyLogosSuccess, (state, { companies }) => ({
    ...state,
    user: state.user
      ? {
          ...state.user,
          EMPRESAS: companies,
        }
      : state.user,
  })),
  on(AuthActions.loadCompanyLogosFailure, (state) => ({
    ...state,
    // Keep existing companies on logo load failure
  })),
  on(AuthActions.setShowErrorLogin, (state, { show }) => ({
    ...state,
    showErrorLogin: show,
  })),
  on(AuthActions.resetState, () => ({
    ...initialState,
  })),
  on(AuthActions.loadDeclaracionApiToken, (state) => ({
    ...state,
    loadingDeclaracionApiToken: true,
  })),
  on(AuthActions.loadDeclaracionApiTokenSuccess, (state, { token }) => ({
    ...state,
    declaracionApiToken: token,
    loadingDeclaracionApiToken: false,
  })),
  on(AuthActions.loadDeclaracionApiTokenFailure, (state) => ({
    ...state,
    loadingDeclaracionApiToken: false,
  })),
  on(AuthActions.refreshDeclaracionApiToken, (state) => ({
    ...state,
    declaracionApiToken: '',
    loadingDeclaracionApiToken: true,
  })),

  on(AuthActions.setDeclaracionApiToken, (state, { token }) => ({
    ...state,
    declaracionApiToken: token,
    loadingDeclaracionApiToken: false,
  })),
  on(AuthActions.setCompaniesParams, (state, { params }) => ({
    ...state,
    companiesParams: params,
  })),
  on(AuthActions.updateCompaniesParams, (state, { params }) => ({
    ...state,
    companiesParams: {
      ...state.companiesParams,
      ...params,
    },
  })),
  on(AuthActions.resetCompaniesParams, (state) => ({
    ...state,
    companiesParams: companiesParamsDefault,
  })),
);
